import React, { useEffect, useRef, useState } from "react";
import "./PredictiveFilmIntelligence.css";

const SCHEMA_VERSION = "aisr-film-intelligence/v1";
const CREATIVE_FIELDS = ["themes", "emotionalDrivers", "characterAffinity", "moments", "messaging"];
const NOT_SUPPLIED = "Not supplied";
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const isText = (value) => typeof value === "string" && value.trim().length > 0;
const textLabel = (value) => isText(value) ? value : NOT_SUPPLIED;
const SIGNAL_LABELS = { audiences: "segmentName", themes: "theme", emotionalDrivers: "emotion", characterAffinity: "character", messaging: "concept", comparableTitles: "title" };

// Guard only the AISR presentation boundary. Missing/malformed optional values
// do not invalidate valid sibling evidence or change the canonical API data.
function signalCount(profile, field) {
  const items = profile[field];
  if (!Array.isArray(items)) return undefined;
  const count = items.filter((item) => isRecord(item) && (field === "moments"
    ? Number.isFinite(item.startSeconds) && Number.isFinite(item.endSeconds)
    : isText(item[SIGNAL_LABELS[field]]))).length;
  return items.length && !count ? undefined : count;
}
const stable = (value) => Array.isArray(value) ? value.map(stable) : value && typeof value === "object"
  ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, stable(value[key])])) : value;

export function isFilmIntelligenceScopeReady(campaign, campaignId, advertiserId) {
  const loadedId = campaign?.id ?? campaign?.campaign_id ?? campaign?.campaignId;
  const loadedAdvertiserId = campaign?.advertiser_id ?? campaign?.advertiserId ?? campaign?.advertiserID;
  return Boolean(campaignId && advertiserId && loadedId != null && loadedAdvertiserId != null &&
    String(loadedId) === String(campaignId) && String(loadedAdvertiserId) === String(advertiserId));
}

export function formatFilmIntelligenceScore(score) {
  if (!isRecord(score) || !isRecord(score.scale) || !Number.isFinite(score.value) || !Number.isFinite(score.scale.min) || !Number.isFinite(score.scale.max) || !isText(score.scale.unit)) return NOT_SUPPLIED;
  return `${score.value} ${score.scale.unit} (${score.scale.min}–${score.scale.max})`;
}

function dateLabel(value) {
  if (!isText(value) || Number.isNaN(new Date(value).getTime())) return NOT_SUPPLIED;
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }).format(new Date(value)) + " UTC";
}

function countLabel(value) {
  return Number.isInteger(value) && value >= 0 ? value.toLocaleString("en-US") : NOT_SUPPLIED;
}

function Metric({ label, value, note }) {
  return <div className="film-intelligence-metric"><dt>{label}</dt><dd>{value}</dd>{note && <small>{note}</small>}</div>;
}

function EvidenceNotes({ label, items }) {
  const notes = Array.isArray(items) ? items.filter(isText) : [];
  if (!notes.length) return null;
  return <div><h4>{label}</h4><ul>{notes.map((note, index) => <li key={index}>{note}</li>)}</ul></div>;
}

function Analysis({ item }) {
  const { profile } = item;
  const summary = isRecord(item.summary) ? item.summary : {};
  const creativeCounts = CREATIVE_FIELDS.map((key) => signalCount(profile, key)).filter((count) => count !== undefined);
  const creativeCount = creativeCounts.reduce((count, value) => count + value, 0);
  const evidence = Array.isArray(item.evidence) ? item.evidence.filter((signal) =>
    isRecord(signal) && isText(signal.id) && isText(signal.signal)) : [];
  return (
    <article className="film-intelligence-analysis" aria-label={`Film analysis: ${profile.film.title}`}>
      <div className="film-intelligence-analysis-heading">
        <h3>{profile.film.title}</h3>
        <span className="film-intelligence-status">Analysis received</span>
      </div>
      <dl className="film-intelligence-metrics">
        <Metric label="Audience signals" value={countLabel(signalCount(profile, "audiences"))} />
        <Metric label="Creative insights" value={creativeCounts.length ? countLabel(creativeCount) : NOT_SUPPLIED} note={creativeCounts.length > 0 && creativeCounts.length < CREATIVE_FIELDS.length ? "Some insight categories not supplied" : null} />
        <Metric label="Comparable titles" value={countLabel(signalCount(profile, "comparableTitles"))} />
      </dl>
      <div className="film-intelligence-evidence-summary">
        <h4>Confidence &amp; evidence</h4>
        <p>{countLabel(summary.evidenceCount)} evidence signals · {countLabel(summary.signalsWithConfidence)} with confidence · {countLabel(summary.signalsWithSampleSize)} with sample size</p>
        <p className="film-intelligence-muted">Scores retain their reported scales. Confidence describes the supplied evidence; it is not a guarantee of campaign performance.</p>
      </div>
      <dl className="film-intelligence-metadata">
        <Metric label="Analysis date" value={dateLabel(profile.analysisDate)} />
        <Metric label="Source" value="aiScreeningRoom" />
        <Metric label="Schema version" value={profile.schemaVersion} />
        <Metric label="Analysis version" value={textLabel(profile.analysisVersion)} />
        <Metric label="Model version" value={isText(profile.modelVersion) ? profile.modelVersion : textLabel(profile.methodology?.modelVersion)} />
        <Metric label="Study sample size" value={countLabel(profile.methodology?.sampleSize)} />
        <Metric label="Study confidence" value={formatFilmIntelligenceScore(profile.methodology?.confidenceLevel)} />
        <Metric label="Analysis ID" value={textLabel(profile.analysisId)} />
      </dl>
      <details className="film-intelligence-details">
        <summary>View evidence &amp; provenance</summary>
        <p className="film-intelligence-muted">Evidence references support future “Recommended because…” rationale. Campaign recommendations remain subject to Cphere strategy review and approval.</p>
        <dl className="film-intelligence-metadata">
          <Metric label="Methodology" value={textLabel(profile.methodology?.studyMethodology)} />
          <Metric label="Methodology source" value={textLabel(profile.methodology?.source)} />
          <Metric label="Analysis timestamp" value={dateLabel(profile.methodology?.analysisTimestamp)} />
          <Metric label="Received" value={dateLabel(item.receivedAt)} />
        </dl>
        <EvidenceNotes label="Limitations" items={profile.methodology?.limitations} />
        <EvidenceNotes label="Study notes" items={profile.methodology?.notes} />
        {evidence.length ? <ul className="film-intelligence-evidence-list">{evidence.map((signal) => (
          <li key={signal.id}>
            <strong>{signal.signal}</strong>
            <dl className="film-intelligence-metadata">
              <Metric label="Metric" value={textLabel(signal.metric)} />
              <Metric label="Score" value={formatFilmIntelligenceScore(signal.score)} />
              <Metric label="Confidence" value={formatFilmIntelligenceScore(signal.confidence)} />
              <Metric
                label={signal.provenanceOrigins?.sampleSize === "/methodology/sampleSize" ? "Study sample size (inherited)" : "Signal sample size"}
                value={countLabel(signal.sampleSize)}
                note={signal.provenanceOrigins?.sampleSize === "/methodology/sampleSize" ? "Study-wide sample; cohort sample not supplied" : null}
              />
              <Metric label="Evidence reference" value={signal.id} />
              <Metric label="Source" value={textLabel(signal.source)} />
              <Metric label="Study source" value={textLabel(signal.studySource)} />
              <Metric label="Model version" value={textLabel(signal.modelVersion)} />
              <Metric label="Timestamp" value={dateLabel(signal.timestamp)} />
              <Metric label="Methodology" value={textLabel(signal.methodology)} />
              <Metric label="Canonical field" value={textLabel(signal.path)} />
            </dl>
            <EvidenceNotes label="Supporting evidence" items={signal.supportingEvidence} />
            <EvidenceNotes label="Signal limitations" items={signal.limitations} />
            <EvidenceNotes label="Signal notes" items={signal.notes} />
          </li>
        ))}</ul> : <p className="film-intelligence-muted">Individual evidence details were not supplied.</p>}
      </details>
    </article>
  );
}

function FilmIntelligenceSource({ baseUrl = "", campaignId, advertiserId, token, authScope, ready = true, loading = false }) {
  const key = JSON.stringify(stable({ baseUrl, campaignId, advertiserId, token, authScope }));
  const [result, setResult] = useState(null);
  const canRead = Boolean(ready && !loading && campaignId && advertiserId && token);
  const scopeRef = useRef({ key: null, canRead: false, generation: 0 });
  if (scopeRef.current.key !== key || scopeRef.current.canRead !== canRead) {
    scopeRef.current = { key, canRead, generation: scopeRef.current.generation + 1 };
  }
  const generation = scopeRef.current.generation;
  useEffect(() => {
    if (!canRead) return undefined;
    let active = true;
    const controller = new AbortController();
    fetch(`${baseUrl.replace(/\/+$/, "")}/api/film-intelligence/campaigns/${encodeURIComponent(campaignId)}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      credentials: "omit", cache: "no-store", signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) throw new Error("Intelligence unavailable");
      const body = await response.json();
      if (body?.ok !== true || !Array.isArray(body.profiles) || body.profiles.some((item) =>
        !isText(item?.id) || item.profile?.schemaVersion !== SCHEMA_VERSION || item.profile?.externalSource !== "aiscreeningroom" ||
        item.profile?.dataClassification !== "aggregated" || typeof item.profile?.film?.title !== "string"
      )) throw new Error("Invalid intelligence response");
      if (active) setResult({ key, generation, status: "available", profiles: body.profiles });
    }).catch(() => { if (active) setResult({ key, generation, status: "unavailable" }); });
    return () => { active = false; controller.abort(); };
  }, [baseUrl, campaignId, token, key, canRead, generation]);

  // Withdraw prior campaign and authentication data during render, before an
  // effect cleanup or a late response can expose it in a new scope.
  if (!canRead) return null;
  const current = result?.key === key && result?.generation === generation ? result : { status: "loading" };
  return (
    <section className="film-intelligence-card" aria-label="Predictive Film Intelligence">
      <header className="film-intelligence-header">
        <div><span className="film-intelligence-eyebrow">Campaign intelligence</span><h2>Predictive Film Intelligence</h2><p>Powered by aiScreeningRoom</p></div>
        <span className="film-intelligence-review-badge">Human review required</span>
      </header>
      {current.status === "loading" && <p role="status">Loading predictive intelligence…</p>}
      {current.status === "unavailable" && <p role="status">Predictive intelligence is currently unavailable. Existing campaign planning remains available.</p>}
      {current.status === "available" && (current.profiles.length ? <>
        {current.profiles.map((item) => <Analysis key={item.id} item={item} />)}
        <p className="film-intelligence-governance">Predictive evidence is available for strategy review. Strategy approval and campaign setup follow Cphere’s existing review process.</p>
      </> : <div className="film-intelligence-empty"><h3>No analysis received</h3><p>This campaign has no predictive film intelligence available yet.</p></div>)}
    </section>
  );
}

export default function PredictiveFilmIntelligence(props) {
  if (process.env.REACT_APP_AISR_FILM_INTELLIGENCE_ENABLED !== "true") return null;
  return <FilmIntelligenceSource {...props} />;
}
